/**
 * What each "New …" opens with.
 *
 * Prefilled SQL instead of a form, and the template is doing the teaching: it
 * shows a primary key that is a `bigserial`, a `timestamptz` rather than a
 * `timestamp`, and the four lines a plpgsql trigger function cannot be written
 * without. Someone who has never written a trigger function gets a working one
 * to edit; someone who has, deletes it and writes their own.
 *
 * They are strings, not generators: a template that computed itself from the
 * open table would be a DDL generator with a text box in front of it, which is
 * exactly the thing this screen decided not to build.
 */
export const NEW_OBJECT_TEMPLATES = {
  table: {
    title: 'New table',
    blurb: 'Creates a table in the public schema.',
    action: 'Create',
    sql: `CREATE TABLE public.new_table (
  id         bigserial PRIMARY KEY,
  name       text NOT NULL,
  created_at timestamptz NOT NULL DEFAULT now()
);`,
  },
  view: {
    title: 'New view',
    blurb: 'A view is a saved query — it stores no data of its own.',
    action: 'Create',
    sql: `CREATE VIEW public.new_view AS
SELECT id, name
  FROM public.new_table
 WHERE created_at > now() - interval '30 days';`,
  },
  function: {
    title: 'New function',
    blurb:
      'Functions belong to the database, not to a table. Trigger functions must return trigger.',
    action: 'Create',
    sql: `CREATE OR REPLACE FUNCTION public.set_updated_at()
RETURNS trigger
LANGUAGE plpgsql
AS $$
BEGIN
  NEW.updated_at = now();
  RETURN NEW;
END;
$$;`,
  },
};

/**
 * A "New …" template, named after what was searched for when the explorer
 * found nothing: "Create table invoices" opens with `invoices` already in it.
 * The name is only placed where the template's own placeholder name is.
 */
export const newObjectTemplate = (kind: 'table' | 'view' | 'function', name?: string) => {
  const template = NEW_OBJECT_TEMPLATES[kind];
  const cleaned = (name || '').trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '');

  if (!cleaned) {
    return template;
  }

  const placeholder = { table: 'new_table', view: 'new_view', function: 'set_updated_at' }[kind];

  return {
    ...template,
    title: `New ${kind} ${cleaned}`,
    sql: template.sql.replace(`public.${placeholder}`, `public.${cleaned}`),
  };
};

/** The two that are about the table on screen, so they carry its name. */
export const newIndexFor = (target: string, table: string) => ({
  title: 'New index',
  blurb:
    'CONCURRENTLY builds it without blocking writes on the table — slower, and worth it on a live app.',
  action: 'Create',
  sql: `CREATE INDEX CONCURRENTLY ${table}_column_idx
  ON ${target} (column_name);`,
});

export const newTriggerFor = (target: string, table: string) => ({
  title: 'New trigger',
  blurb: 'A trigger runs an existing function. Create the function first.',
  action: 'Create',
  sql: `CREATE TRIGGER ${table}_set_updated_at
  BEFORE UPDATE ON ${target}
  FOR EACH ROW
  EXECUTE FUNCTION public.set_updated_at();`,
});
